/**
 * «تنظیمات پیش‌فرض پارسینا» — what a new clinic starts with when its founder
 * leaves the tick on in the last registration step.
 *
 * One place, because the same values are both APPLIED (the roles are seeded, the
 * founder-doctor's link is written) and SHOWN (the summary under the tick, and
 * the starting point of the editors when the tick is taken off). A default that
 * says one thing and writes another is worse than no default.
 *
 * Every value here is a starting point, never a constraint: the roles are
 * editable in «همکاران ← نقش‌ها», and the hours and visit length in the doctor's
 * row on «همکاران».
 */

import WORK_WEEK from '../../constants/workWeek.json';

/** مدت هر ویزیت, in minutes — one of the booking module's DURATION_OPTIONS. */
export const DEFAULT_VISIT_MINUTES = 15;

/** Iranian weekdays, 0 = شنبه: شنبه تا چهارشنبه. */
export const DEFAULT_PRESENCE_DAYS = [0, 1, 2, 3, 4];

/** ISO weekday (1 = Monday … 7 = Sunday) → the wire's day index (0 = شنبه … 6 = جمعه). */
const fromIsoWeekday = (iso) => (iso + 1) % 7;

/**
 * The working days a founder from abroad starts with: every day but their
 * country's weekend, as CLDR records it (constants/workWeek.json, generated by
 * scripts/gen-work-week.mjs), in the order their week runs — starting the day
 * after the weekend, so Iraq reads Sunday to Thursday and India Monday to
 * Saturday. A country with no entry, or none known, has a Saturday–Sunday
 * weekend: Monday to Friday.
 *
 * Iran is not asked: شنبه تا چهارشنبه is this product's own default for an
 * Iranian clinic (CLDR gives Iran a Friday-only weekend, and Thursday is a half
 * day most clinics do not book), so it stays exactly what it always was.
 */
export function presenceDaysForCountry(country) {
  if (country === 'IR') return DEFAULT_PRESENCE_DAYS;
  const iso = WORK_WEEK.weekends[country] || [6, 7];
  const weekend = iso.map(fromIsoWeekday);
  const lastOff = fromIsoWeekday(Math.max(...iso));
  const days = [];
  for (let i = 1; i <= 7; i += 1) {
    const day = (lastOff + i) % 7;
    if (!weekend.includes(day)) days.push(day);
  }
  return days;
}

/** One shift a day, 9 AM to 6 PM. */
export const DEFAULT_SHIFT = { startTime: '09:00', endTime: '18:00' };

/** The default week as the wire list `updateDoctorLink({ presences })` takes. */
export const defaultPresences = (days = DEFAULT_PRESENCE_DAYS) => days.map((dayOfWeek) => ({
  dayOfWeek, ...DEFAULT_SHIFT,
}));

/**
 * «ویزیت» — the standard consultation row every clinic starts with declared.
 * The same catalogue id the contract-removal migration gives a clinic's visit
 * (accounting c2a7e5d91f40), so a clinic born here and one converted there have
 * one and the same visit service.
 */
export const VISIT_CATALOG_ID = 'consult-visit';

/**
 * Its price, in TOMAN — what the founder reads and types. Accounting stores
 * rials. A default for an IRR clinic ONLY: a clinic keeping its books in any
 * other currency is asked for its own price.
 */
export const DEFAULT_VISIT_PRICE_TOMAN = 600000;

/**
 * The clinic-wide «ویزیت» declaration, as `setClinicServiceDeclaration` takes it.
 *
 * Without `doctorId` it is written INACTIVE with no performer: accounting
 * refuses an active service nobody performs, and a clinic registered without
 * «من پزشک هستم» has nobody yet. It is still declared and priced, so the first
 * doctor the clinic adds is one performer and one switch away from a billable
 * visit. With `doctorId` — the founder-doctor, once joined — it is active and
 * theirs by default.
 *
 * `orderType: 'consultation'` is what marks it as the clinic's VISIT rather than
 * an ordinary service; the description is stored Persian because it is the
 * clinic's own name for the line on every invoice, whoever reads it.
 *
 * `name` replaces that Persian word for a clinic of a brand outside the Iranian
 * market (`market: 'intl'`): nobody there reads Persian, so the founder's
 * registration passes the word in the panel's own language (ClinicRolesStep).
 */
export const visitDeclarationInput = ({ priceMinor, durationMinutes, doctorId = null, name }) => ({
  catalogId: VISIT_CATALOG_ID,
  description: name || 'ویزیت',  // i18n-exempt: the clinic's STORED service name, read by every member and printed on invoices — data, not UI copy
  category: name || 'ویزیت',     // i18n-exempt: the catalogue's own category name, stored as data
  orderType: 'consultation',
  unitPrice: priceMinor,
  durationMinutes,
  performerRole: 'doctor',
  schedulingMode: 'TIMED',
  isActive: Boolean(doctorId),
  performers: doctorId ? [{ type: 'DOCTOR', id: doctorId, isDefault: true }] : [],
});

/** The founder-doctor's settings as the defaults would write them. */
export const defaultDoctorSetup = (days = DEFAULT_PRESENCE_DAYS) => ({
  presences: defaultPresences(days),
  visitDurationMinutes: DEFAULT_VISIT_MINUTES,
});
